import type { CSSProperties } from 'react';

import classnames from 'classnames';

import { getContrastingColor } from '../utils/colorContrast';
import { computeFont } from '../utils/fonts';
import { getWidthStyle } from '../utils/sizing';

import * as styles from './Survey.css';
import type { SurveyElementProps } from './Survey.types';
import SurveyRenderer from './SurveyRenderer';

export function SurveyElement(props: SurveyElementProps) {
  const {
    element,
    children,
    attributes,
    className,
    email,
    isSubmitting,
    submittingText,
    hasLocalStorageEmail,
    isLive,
  } = props;

  const {
    answerBackgroundColor,
    answerTextColor,
    answerTextFontFamily,
    height,
    width,
    customAttributes,
    gap,
    data,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    shadow,
    inputBorderRadius,
    inputBorderWidth,
    inputBorderColor,
    inputBorderStyle,
    background,
    labelColor,
    labelFontFamily,
    helperTextColor,
    helperTextFontFamily,
    buttonColor,
    requiredColor,
    buttonTextColor,
    buttonTextFontFamily,
    padding,
    textAlign,
    inputBackgroundColor,
    inputTextColor,
    inputTextFontFamily,
    inputPlaceholderColor,
    titleColor,
    titleFontFamily,
    titleFontSize,
    descriptionColor,
    descriptionFontFamily,
    descriptionFontSize,
    mobilePadding,
  } = element.attrs || {};

  const radioOptionJustifyContent = textAlign === 'center' ? 'center' : 'flex-start';
  const radioOptionFlexDirection = textAlign === 'right' ? 'row-reverse' : 'row';
  const radioCheckedDotColor = getContrastingColor(inputBorderColor);

  return (
    <div
      {...customAttributes}
      className={classnames(
        className,
        customAttributes?.className,
        'flex flex-col gap-6',
        !isLive && 'pointer-events-none',
        styles.survey,
      )}
      style={
        {
          height: `${height}px`,
          position: 'relative',
          borderRadius,
          borderWidth,
          borderColor,
          borderStyle,
          boxShadow: shadow,
          background,
          '--answer-background-color': answerBackgroundColor,
          '--answer-text-color': answerTextColor,
          '--answer-text-font-family': computeFont(answerTextFontFamily),
          '--gap': gap,
          '--text-align': textAlign,
          '--radio-option-justify-content': radioOptionJustifyContent,
          '--radio-option-flex-direction': radioOptionFlexDirection,
          '--question-label-color': labelColor,
          '--question-label-font-family': computeFont(labelFontFamily),
          '--question-required-color': requiredColor,
          '--helper-text-color': helperTextColor,
          '--helper-text-font-family': computeFont(helperTextFontFamily),
          '--button-color': buttonColor,
          '--button-text-color': buttonTextColor,
          '--button-text-font-family': computeFont(buttonTextFontFamily),
          '--input-background-color': inputBackgroundColor,
          '--input-text-color': inputTextColor,
          '--input-text-font-family': computeFont(inputTextFontFamily),
          '--input-placeholder-color': inputPlaceholderColor,
          '--border-radius': inputBorderRadius,
          '--border-width': inputBorderWidth,
          '--border-color': inputBorderColor,
          '--border-style': inputBorderStyle,
          '--radio-checked-dot-color': radioCheckedDotColor,
          '--shadow': shadow,
          '--title-color': titleColor,
          '--title-font-family': computeFont(titleFontFamily),
          '--description-color': descriptionColor,
          '--description-font-family': computeFont(descriptionFontFamily),
          '--mobile-padding': mobilePadding ?? padding,
          '--padding': padding,
          ...getWidthStyle(width),
        } as CSSProperties
      }
      {...attributes}
    >
      <>
        <div className="flex flex-col gap-2">
          <h4 className={classnames(styles.titleStyle, titleFontSize || 'text-3xl', 'font-bold')}>{data.title}</h4>
          <p className={classnames(styles.descriptionStyle, descriptionFontSize || 'text-lg', 'font-semibold')}>
            {data.description}
          </p>
        </div>

        <SurveyRenderer
          survey={data}
          email={email}
          hasLocalStorageEmail={hasLocalStorageEmail}
          isSubmitting={isSubmitting}
          submittingText={submittingText}
          ctaText={data.cta_text || 'Submit'}
        />
      </>
      {children}
    </div>
  );
}
